// frontend/src/components/MeetingTemplateList/MeetingTemplateList.js
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../apiClient';
import { UserContext } from '../../UserContext';
import { Dropdown, ButtonGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import './MeetingTemplateList.css';

function MeetingTemplateList() {
  const [templates, setTemplates] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState('');
  const [error, setError] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  /* ---- Fetch helpers ---- */
  const fetchClinics = () =>
    apiClient.get('/api/clinics').then(res => setClinics(res.data));

  const fetchTemplates = () =>
    apiClient.get('/api/meetingmallar')
      .then(res => setTemplates(res.data))
      .catch(() => setError('Kunde inte hämta mötesmallar.'));

  useEffect(() => {
    fetchClinics();
    fetchTemplates();
  }, []);

  /* ---- Copy ---- */
  const copyTemplate = async (templateId, clinicId) => {
    try {
      await apiClient.post(`/api/meetingmallar/${templateId}/copy`, { clinic_id: clinicId });
      toast.success('Mötesmall kopierad!');
      fetchTemplates();
    } catch {
      toast.error('Kunde inte kopiera.');
    } finally {
      setOpenDropdownId(null);
    }
  };

  /* ---- Delete ---- */
  const handleDelete = async (id) => {
    if (!window.confirm('Ta bort mötesmall?')) return;
    try {
      await apiClient.delete(`/api/meetingmallar/${id}`);
      toast.success('Mötesmall borttagen');
      setTemplates(prev => prev.filter(t => t.meeting_template_id !== id));
    } catch {
      toast.error('Kunde inte ta bort.');
    }
  };

  const filtered = selectedClinicId
    ? templates.filter(t => t.clinic_id === parseInt(selectedClinicId, 10))
    : templates;

  const clinicMap = clinics.reduce((acc, c) => ({ ...acc, [c.clinic_id]: c.clinic_name }), {});

  return (
    <div className="meeting-template-list-container">
      <h2>Mötesmallar</h2>

      <div className="filter-row">
        <label>Filtrera klinik:</label>
        <select value={selectedClinicId} onChange={(e) => setSelectedClinicId(e.target.value)}>
          <option value="">-- Alla --</option>
          {clinics.map(c => (
            <option key={c.clinic_id} value={c.clinic_id}>{c.clinic_name}</option>
          ))}
        </select>

        {user?.is_admin && (
          <button className="create-button" onClick={() => navigate('/meetingmallar/create')}>
            Skapa ny mötesmall
          </button>
        )}
      </div>

      {error && <p className="error-message">{error}</p>}

      <table className="meeting-template-table">
        <thead>
          <tr>
            <th>Mallnamn</th>
            <th>Klinik</th>
            <th>Åtgärder</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(t => (
            <tr key={t.meeting_template_id}>
              <td>{t.template_name}</td>
              <td>{clinicMap[t.clinic_id] || 'Global'}</td>
              <td>
                <div className="action-buttons">
                  {user?.is_admin && (
                    <Dropdown
                      as={ButtonGroup}
                      show={openDropdownId === t.meeting_template_id}
                      onToggle={(isOpen, _, meta = {}) => {
                        if (meta.source === 'select') return;
                        setOpenDropdownId(isOpen ? t.meeting_template_id : null);
                      }}
                    >
                      <button
                        className="copy-button"
                        onClick={() =>
                          setOpenDropdownId(prev =>
                            prev === t.meeting_template_id ? null : t.meeting_template_id)}
                      >
                        Kopiera till…
                      </button>
                      <Dropdown.Menu>
                        {clinics.map(c => (
                          <Dropdown.Item
                            key={c.clinic_id}
                            onClick={() => copyTemplate(t.meeting_template_id, c.clinic_id)}
                          >
                            {c.clinic_name}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  )}

                  <button
                    className="edit-button"
                    onClick={() => navigate(`/meetingmallar/edit/${t.meeting_template_id}`)}
                  >
                    Redigera
                  </button>
                  <button
                    className="delete-button"
                    onClick={() => handleDelete(t.meeting_template_id)}
                  >
                    Ta bort
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default MeetingTemplateList;
